import { Add, Close } from "@mui/icons-material"
import { Box, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { FaIcon } from "../../TagChip"

type Tag = {
  id: number
  label: string
  color: string
  icon?: string
  shortLabel?: string
  displayOption?: string
}

type Props = {
  selectedTagIds: number[]
  availableTags: Tag[]
  onChange: (tagId: number) => void
  label?: string
}

export const TagSelector: FC<Props> = ({
  selectedTagIds,
  availableTags,
  onChange,
  label,
}) => {
  const { t } = useTranslation()

  const selectedTags = availableTags.filter((tag) => selectedTagIds.includes(tag.id))
  const unselectedTags = availableTags.filter((tag) => !selectedTagIds.includes(tag.id))

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
          {label}
        </Typography>
      )}
      {selectedTags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
          {selectedTags.map((tag) => (
            <Box
              key={tag.id}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: tag.color || "#757575",
                color: "#fff",
                borderRadius: 1,
                height: 26,
                px: 0.75,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1,
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
              onClick={() => onChange(tag.id)}
            >
              <FaIcon icon={tag.icon || "tag"} color="#fff" size={11} />
              <span>{String(t(tag.shortLabel || tag.label, tag.shortLabel || tag.label))}</span>
              <Close sx={{ fontSize: 13, ml: 0.25, color: "rgba(255,255,255,0.7)" }} />
            </Box>
          ))}
        </Stack>
      )}
      {unselectedTags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {unselectedTags.map((tag) => (
            <Box
              key={tag.id}
              onClick={() => onChange(tag.id)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                border: 1,
                borderColor: tag.color || "#757575",
                color: tag.color || "#757575",
                borderRadius: 1,
                height: 26,
                px: 0.75,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1,
                whiteSpace: "nowrap",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: `${tag.color || "#757575"}14`,
                },
              }}
            >
              <FaIcon icon={tag.icon || "tag"} size={11} />
              <span>{String(t(tag.shortLabel || tag.label, tag.shortLabel || tag.label))}</span>
              <Add sx={{ fontSize: 13, ml: 0.25 }} />
            </Box>
          ))}
        </Stack>
      )}
      {availableTags.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {t("orderCreate.noTags")}
        </Typography>
      )}
    </Box>
  )
}
